import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMyPermissions } from '../services/api';
import { getToken } from '../utils/auth';

var LOGIN_DATA_KEY = 'adminLoginData';

var readLoginData = function () {
  try { return JSON.parse(localStorage.getItem(LOGIN_DATA_KEY) || 'null'); }
  catch (e) { return null; }
};

var getId = function (value) {
  if (!value) return null;
  if (typeof value === 'object') return value._id || value.id || value.adminId || null;
  return value;
};

export var normalizeModuleKey = function (value) {
  return String(value || '').trim().toLowerCase();
};

export var buildPermMap = function (permissions) {
  var map = {};
  if (!Array.isArray(permissions)) return map;
  permissions.forEach(function (entry) {
    if (!entry || !entry.module) return;
    var key = normalizeModuleKey(entry.module);
    var actionMap = map[key] || {};
    var actions = Array.isArray(entry.actions) ? entry.actions : [];
    actions.forEach(function (action) {
      actionMap[String(action || '').trim().toLowerCase()] = true;
    });
    map[key] = actionMap;
  });
  return map;
};

var isSuperAdminFromData = function (data) {
  if (!data) return false;
  if (data.isSuperAdmin === true || data.isSuperAdmin === 'true') return true;
  if (data.role) {
    if (typeof data.role === 'string') {
      var rStr = data.role.toLowerCase();
      if (rStr === 'super_admin' || rStr === 'superadmin' || rStr.indexOf('super admin') !== -1) return true;
    } else if (typeof data.role === 'object') {
      var slug = String(data.role.slug || '').toLowerCase();
      var name = String(data.role.name || '').toLowerCase();
      if (slug === 'super_admin' || slug === 'superadmin' || name.indexOf('super admin') !== -1) {
        return true;
      }
    }
  }
  return false;
};

var normalizePermissionsResponse = function (res) {
  var raw = res && res.data ? res.data : res;
  var data = raw && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw;
  data = data || {};
  return {
    adminId: data.adminId || data.id || getId(data.admin),
    role: data.role || null,
    permissions: Array.isArray(data.permissions) ? data.permissions : (data.role && Array.isArray(data.role.permissions) ? data.role.permissions : []),
    isSuperAdmin: isSuperAdminFromData(data),
  };
};

var PermissionContext = createContext({
  hasPermission: function () { return true; },
  isSuperAdmin: true,
  role: null,
  permissions: [],
  adminId: null,
  permissionsLoaded: false,
  refreshPermissions: function () {},
});

export var usePermission = function () { return useContext(PermissionContext); };

export var PermissionProvider = function (props) {
  var initial = readLoginData() || {};
  var initialIsSuper = isSuperAdminFromData(initial);
  var initialPerms = Array.isArray(initial.permissions) ? initial.permissions : [];

  var _ps = useState(initialPerms); var permissions = _ps[0]; var setPermissions = _ps[1];
  var _pm = useState(function () { return buildPermMap(initialPerms); }); var permMap = _pm[0]; var setPermMap = _pm[1];
  var _sa = useState(initialIsSuper); var isSuperAdmin = _sa[0]; var setIsSuperAdmin = _sa[1];
  var _ro = useState(initial.role || null); var role = _ro[0]; var setRole = _ro[1];
  var _ai = useState(initial.id || null); var adminId = _ai[0]; var setAdminId = _ai[1];
  var _pl = useState(false); var permissionsLoaded = _pl[0]; var setPermissionsLoaded = _pl[1];

  var applyPermissionState = useCallback(function (next) {
    var nextPerms = Array.isArray(next.permissions) ? next.permissions : [];
    setPermissions(nextPerms);
    setPermMap(buildPermMap(nextPerms));
    setRole(next.role || null);
    setAdminId(next.adminId || null);
    setIsSuperAdmin(next.isSuperAdmin === true || isSuperAdminFromData(next));
  }, []);

  var clearPermissionState = useCallback(function () {
    applyPermissionState({ adminId: null, role: null, permissions: [], isSuperAdmin: false });
    setPermissionsLoaded(true);
  }, [applyPermissionState]);

  var fetchFromServer = useCallback(function (force) {
    var token = getToken();
    var isLoginPage = typeof window !== 'undefined' && window.location && window.location.pathname === '/admin/login';

    if (!token || (isLoginPage && !force)) {
      clearPermissionState();
      return Promise.resolve(null);
    }

    return getMyPermissions()
      .then(function (res) {
        var fresh = normalizePermissionsResponse(res);
        applyPermissionState(fresh);
        try {
          var existing = readLoginData() || {};
          localStorage.setItem(LOGIN_DATA_KEY, JSON.stringify(Object.assign({}, existing, {
            id: fresh.adminId || existing.id,
            role: fresh.role,
            permissions: fresh.permissions,
            isSuperAdmin: fresh.isSuperAdmin,
          })));
        } catch (e) {}
        return fresh;
      })
      .catch(function () {
        var cached = readLoginData();
        if (cached && (cached.permissions || cached.isSuperAdmin || isSuperAdminFromData(cached))) {
          applyPermissionState({
            adminId: cached.id,
            role: cached.role,
            permissions: cached.permissions || [],
            isSuperAdmin: isSuperAdminFromData(cached),
          });
        } else if (!token) {
          clearPermissionState();
        }
        return null;
      })
      .finally(function () { setPermissionsLoaded(true); });
  }, [applyPermissionState, clearPermissionState]);

  useEffect(function () {
    fetchFromServer(false);
  }, [fetchFromServer]);

  useEffect(function () {
    var handler = function () { clearPermissionState(); };
    window.addEventListener('adminLogout', handler);
    return function () { window.removeEventListener('adminLogout', handler); };
  }, [clearPermissionState]);

  useEffect(function () {
    var handler = function () {
      var freshLocal = readLoginData();
      if (freshLocal) {
        applyPermissionState({
          adminId: freshLocal.id,
          role: freshLocal.role,
          permissions: freshLocal.permissions || [],
          isSuperAdmin: isSuperAdminFromData(freshLocal),
        });
      }
      setPermissionsLoaded(false);
      fetchFromServer(true);
    };
    window.addEventListener('adminLogin', handler);
    window.addEventListener('adminPermissionsUpdated', handler);
    return function () {
      window.removeEventListener('adminLogin', handler);
      window.removeEventListener('adminPermissionsUpdated', handler);
    };
  }, [applyPermissionState, fetchFromServer]);

  var hasPermission = useCallback(function (module, action) {
    if (isSuperAdmin) return true;
    if (!module) return false;
    var modulePerms = permMap[normalizeModuleKey(module)];
    if (!modulePerms) return false;
    if (!action) return Object.keys(modulePerms).length > 0;
    return !!modulePerms[String(action).trim().toLowerCase()];
  }, [isSuperAdmin, permMap]);

  return React.createElement(PermissionContext.Provider, { value: {
    hasPermission: hasPermission,
    isSuperAdmin: isSuperAdmin,
    role: role,
    permissions: permissions,
    adminId: adminId,
    permissionsLoaded: permissionsLoaded,
    refreshPermissions: fetchFromServer,
  } }, props.children);
};

export default PermissionContext;
